'use client'

/**
 * The journal slice's write spec: what a journal draft is, and the three writes over it.
 *
 * The mechanics are `document/operations/`' (#289); this is the journal vocabulary over them
 * (ADR 0012:30-36). It names nothing from `legacy/`: `JournalInsert` and `JournalUpdate` were
 * Supabase payload types in `types/entries.ts`, snake_case on an Automerge entry, and a slice that
 * carried them would carry the old store's shape into the new tree.
 *
 * **The directive is load-bearing.** `app/app/journal/page.tsx` is a server component and imports
 * `@/entries`, which pulls every module the face names into the RSC graph — so the three writes are TanStack mutations and
 * this module opens with `'use client'`. Without it `pnpm build` fails collecting `/app/journal`
 * with `createContext is not a function`, and nothing before the build says so (#375).
 */

import { useMutation } from '@tanstack/react-query'

import { useDocumentOps, useDocumentView, type EntryChange } from '@/document'

/**
 * What a form edits and these three writes accept.
 *
 * Create takes the whole draft; update takes a `Partial` of it, and so does
 * `RowProps<JournalEntry, JournalDraft>`'s `onSave`. That asymmetry is real rather than cosmetic:
 * `ops.createEntry` types `title` and `content` as required, so a create that let them be absent
 * would only move the check to a cast. This is where `project_id` died.
 */
export interface JournalDraft {
  title: string
  content: string
  projectId: string | null
  topicIds: string[]
  peopleIds: string[]
  /**
   * Marks the week's generated reflection.
   *
   * A weekly reflection **is** a journal entry — `resurfacing/` decides when one appears and what
   * it says, and the journal slice owns what it is. The flag was written straight onto
   * `metadata` by a `resurfacing/` module until #406; expressing it here is what lets that domain
   * call this write spec instead of the document operations.
   */
  isWeeklyReflection?: boolean
}

export function useCreateJournal() {
  const doc = useDocumentView()
  const ops = useDocumentOps()

  return useMutation({
    mutationFn: async (data: JournalDraft) => {
      if (!doc) {
        throw new Error('Document not ready')
      }

      const id = ops.createEntry({
        type: 'journal',
        title: data.title,
        content: data.content,
        projectId: data.projectId,
        topicIds: data.topicIds,
        peopleIds: data.peopleIds,
        ...(data.isWeeklyReflection
          ? { metadata: { isWeeklyReflection: true } }
          : {}),
      })

      return { id }
    },
  })
}

export function useUpdateJournal() {
  const doc = useDocumentView()
  const ops = useDocumentOps()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<JournalDraft>
    }) => {
      if (!doc) {
        throw new Error('Document not ready')
      }

      const change: EntryChange = {}
      if (data.title !== undefined) change.title = data.title
      if (data.content !== undefined) change.content = data.content
      if (data.projectId !== undefined) change.projectId = data.projectId
      if (data.topicIds !== undefined) change.topicIds = data.topicIds
      if (data.peopleIds !== undefined) change.peopleIds = data.peopleIds

      if (!ops.updateEntry(id, change)) {
        throw new Error(`Journal ${id} not found`)
      }

      return doc.entries[id]
    },
  })
}

export function useDeleteJournal() {
  const doc = useDocumentView()
  const ops = useDocumentOps()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!doc) {
        throw new Error('Document not ready')
      }

      if (!ops.archiveEntry(id)) {
        throw new Error(`Journal ${id} not found`)
      }
    },
  })
}
